/* ── Glyphes d'interface ─────────────────────────────────────────────
   La charte de référence ne place JAMAIS d'emoji dans le chrome : chaque
   signe est une icône dessinée, trait fin uniforme, extrémités arrondies,
   posée le plus souvent dans une pastille ronde pastel.

   Ce fichier remplace les emoji qui traînaient dans l'UI (🛡️ 🟢🟡🔴⚪ ✨
   📊 ⚠️ ✓ ○). Un seul poids de trait — 1.6 — pour que tous les glyphes
   pèsent pareil à l'écran. */

interface GlyphProps {
  className?: string;
}

const BASE = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** Bouclier — pré-screen de disponibilité de marque. */
export function ShieldGlyph({ className = 'h-4 w-4' }: GlyphProps) {
  return (
    <svg className={className} {...BASE}>
      <path d="M12 3l7 3v5.5c0 4.3-2.9 8.2-7 9.5-4.1-1.3-7-5.2-7-9.5V6l7-3z" />
      <path d="M9.2 12.2l2 2 3.6-3.9" />
    </svg>
  );
}

/** Triangle d'alerte — avertissements, disclaimers, états dégradés. */
export function AlertGlyph({ className = 'h-4 w-4' }: GlyphProps) {
  return (
    <svg className={className} {...BASE}>
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      <path d="M12 9v4.5" />
      <path d="M12 17h.01" />
    </svg>
  );
}

/** Coche — état prêt / configuré / conforme. */
export function CheckGlyph({ className = 'h-4 w-4' }: GlyphProps) {
  return (
    <svg className={className} {...BASE}>
      <path d="M4.5 12.5l5 5 10-11" />
    </svg>
  );
}

/** Cercle vide — état non configuré, pendant de la coche. */
export function CircleGlyph({ className = 'h-4 w-4' }: GlyphProps) {
  return (
    <svg className={className} {...BASE}>
      <circle cx="12" cy="12" r="8" />
    </svg>
  );
}

/** Étoile à quatre branches — le « sparkle » rose de la charte, réservé
 *  aux moments où l'IA propose quelque chose. */
export function SparkleGlyph({ className = 'h-4 w-4' }: GlyphProps) {
  return (
    <svg className={className} {...BASE}>
      <path d="M12 2.6c.5 4.2 2.6 6.3 6.8 6.8-4.2.5-6.3 2.6-6.8 6.8-.5-4.2-2.6-6.3-6.8-6.8 4.2-.5 6.3-2.6 6.8-6.8z" />
      <path d="M18.4 15.6c.25 1.8 1.2 2.75 3 3-1.8.25-2.75 1.2-3 3-.25-1.8-1.2-2.75-3-3 1.8-.25 2.75-1.2 3-3z" />
    </svg>
  );
}

/** Histogramme — historique, statistiques. */
export function ChartGlyph({ className = 'h-4 w-4' }: GlyphProps) {
  return (
    <svg className={className} {...BASE}>
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <path d="M8.5 20v-6" />
      <path d="M13 20V8.5" />
      <path d="M17.5 20v-9" />
    </svg>
  );
}

/** Pastille pleine — feu de signalisation des verdicts, en remplacement
 *  des ronds emoji. La couleur vient du parent via `currentColor`. */
export function DotGlyph({ className = 'h-3 w-3' }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="12" r="7" />
    </svg>
  );
}
